import React, { useCallback } from "react";
import * as Scrivito from "scrivito";
import { Row } from "reactstrap";
import cx from "classnames";
import { useProjectSummarySidebarContext } from "providers/ProjectSummarySidebarProvider";
import { ProductCard } from "components/ProductsOverview/ProductCard";
import { useTranslate } from "hooks";
import {
  useProductsOverviewActions,
  useProductsOverviewStore,
} from "stores/productsOverviewStore";
import type { NiceModalHandler } from "@ebay/nice-modal-react";
import {
  productNavigationHandler,
  type ModalProps,
} from "utils/productNavigationHandler";
import { useProject } from "stores/project/project/projectStore";
import { useIsSameProj } from "stores/globalStore";
import type { ProductNode } from "interfaces/product/product.types";
import type { FetchError } from "utils";

type ProductsOverviewBodyProps = {
  error?: FetchError | null;
  id?: string;
  infoText?: string;
  isModalActive?: boolean;
  dummy?: string;
  wizardType?: string;
  modal?: NiceModalHandler<Record<string, unknown>>;
  handleBackAction?: () => void;
  type?: string;
  groupPosition?: number;
};

export const ProductsOverviewBody = Scrivito.connect(
  ({
    error,
    id,
    infoText,
    isModalActive,
    dummy,
    wizardType,
    modal,
    handleBackAction,
    type,
    groupPosition,
  }: ProductsOverviewBodyProps) => {
    const translate = useTranslate();
    const { isExtended } = useProjectSummarySidebarContext();
    const isSameProj = useIsSameProj();
    const {
      setProductCategoryId,
      navigateToConfiguratorPage,
      navigateToProductListPage,
    } = useProductsOverviewActions();
    const project = useProject();

    const productsContent = useProductsOverviewStore(
      (state) => state.productsContent,
    );

    const imageCardHandler = useCallback(
      (product: ProductNode) => {
        productNavigationHandler({
          product: product as Parameters<
            typeof productNavigationHandler
          >[0]["product"],
          isModalActive: isModalActive ?? false,
          id: id ?? "",
          dummy: dummy ?? "",
          wizardType: wizardType ?? "",
          handleBackAction: handleBackAction ?? (() => undefined),
          type: type ?? "",
          groupPosition: groupPosition ?? 0,
          modal: modal! as ModalProps,
          setProductCategoryId,
          navigateToConfiguratorPage,
          projectId: project?.id ?? "",
          navigateToProductListPage,
          seriesMap: new Map(),
          productCategoryId: [],
          findCategoryByName: () => ({ key: "", name: "", class: [] }),
          setQuery: () => undefined,
          setCategoryHeadline: () => undefined,
        });
      },
      [
        isModalActive,
        id,
        dummy,
        wizardType,
        handleBackAction,
        type,
        groupPosition,
        modal,
        setProductCategoryId,
        navigateToConfiguratorPage,
        project?.id,
        navigateToProductListPage,
      ],
    );

    if (error) {
      return (
        <div className="mb-6">
          <span>{translate("ERROR_OCCURED")}</span>
        </div>
      );
    }

    if (infoText) {
      return (
        <Row className="px-3 pt-6">
          {infoText.split(/\r?\n/g).map((parag, index) => (
            <p className="px-2 font16" key={index}>
              {parag}
            </p>
          ))}
        </Row>
      );
    }

    return (
      <Row
        className={cx("px-5", { "overflow-auto": isModalActive })}
        style={{
          ...(isModalActive && { maxHeight: "60vh" }),
        }}
      >
        {productsContent?.map((product) => (
          <ProductCard
            product={product}
            key={
              product.pimId ? `${product.key}+${product.pimId}` : product.key
            }
            isExtended={isExtended}
            imageCardHandler={imageCardHandler}
            isSameProj={isSameProj}
          />
        ))}
      </Row>
    );
  },
);
